import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { getIdTokenResult } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey } from "../config/firebase";
import { emptySupportCase as emptyCase, supportCaseForm, supportCaseScope, sameSupportScope,
	validateSupportCaseForm, newSupportRequestId, supportCaseSubmissionStore } from "./supportCaseSubmission";
import { emptyHistoryFilters, supportHistoryFilters, validateSupportHistory, supportIncidentDraft } from "./supportCaseHistory";
import "./styles/SupportCases.css";

const currentScope = user => {
	if (auth.currentUser !== user) throw new Error("Support project or account changed. Reload and sign in again.");
	return supportCaseScope({ user, auth, functions, environment: selectedAdminEnvironment,
		selectedKey: getSelectedAdminEnvironmentKey() });
};

const formatDate = (value) => {
	if (!value) return "--";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const SupportCasesView = ({ user, scope, incident }) => {
	const [cases, setCases] = useState([]);
	const [form, setForm] = useState(emptyCase);
	const [editingCaseId, setEditingCaseId] = useState("");
	const [expectedRevision, setExpectedRevision] = useState(0);
	const [pending, setPending] = useState(null);
	const [ready, setReady] = useState(false);
	const [storageError, setStorageError] = useState(false);
	const [draftFilters, setDraftFilters] = useState(emptyHistoryFilters);
	const [history, setHistory] = useState({ filters: emptyHistoryFilters, cursors: [null] });
	const historyRef = useRef(history);
	const pageRef = useRef(null);
	const [nextCursor, setNextCursor] = useState(null);
	const [pageLoaded, setPageLoaded] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const alive = useRef(false);
	const busy = useRef(false);
	const loadSequence = useRef(0);
	const lastIncident = useRef();
	const isCurrent = () => {
		try { return alive.current && sameSupportScope(scope, currentScope(user)); } catch (_) { return false; }
	};
	const guard = () => { if (!isCurrent()) throw new Error("Support project or account changed. Reload before continuing."); };
	const store = () => supportCaseSubmissionStore(window.localStorage, scope);
	const verifyActor = async () => {
		guard();
		const token = await getIdTokenResult(user, true);
		guard();
		if (!["admin", "scerv_admin", "godmode", "super_admin"].includes(String(token.claims.role || "").toLowerCase())) {
			throw new Error("Current Scerv admin access is required.");
		}
	};

	const resetEditor = () => { setForm(emptyCase); setEditingCaseId(""); setExpectedRevision(0); };
	const loadCases = async (query = historyRef.current) => {
		const sequence = ++loadSequence.current;
		historyRef.current = query;
		setHistory(query);
		pageRef.current = null;
		setCases([]);
		setNextCursor(null);
		setPageLoaded(false);
		setLoadError("");
		if (editingCaseId && !pending && !busy.current) resetEditor();
		setLoading(true);
		try {
			await verifyActor();
			if (sequence !== loadSequence.current) return;
			const listCases = httpsCallable(functions, "listScervSupportCases");
			const response = await listCases({
				projectId: scope.projectId,
				pageSize: 100,
				...query.filters,
				cursor: query.cursors[query.cursors.length - 1],
			});
			if (sequence !== loadSequence.current) return;
			guard();
			const page = validateSupportHistory(response.data, scope, query.filters);
			if (page.nextCursor !== null && query.cursors.includes(page.nextCursor)) throw new Error("Support history cursor did not advance.");
			pageRef.current = page;
			setCases(page.cases);
			setNextCursor(page.nextCursor);
			setPageLoaded(true);
		} catch (err) {
			if (alive.current && sequence === loadSequence.current) {
				setLoadError(err.message || "Failed to load support cases.");
				if (!isCurrent()) { setReady(false); if (!pending) resetEditor(); }
			}
		} finally {
			if (alive.current && sequence === loadSequence.current) setLoading(false);
		}
	};
	const resetHistory = () => loadCases({ filters: historyRef.current.filters, cursors: [null] });

	useEffect(() => {
		alive.current = true;
		try {
			const recovered = store().read();
			if (recovered) {
				setPending(recovered);
				setForm(recovered.command.supportCase);
				setEditingCaseId(recovered.command.caseId || "");
				setExpectedRevision(recovered.command.expectedRevision);
				setMessage("A support save is awaiting confirmation.");
			}
		} catch (err) { setStorageError(true); setError(err.message); }
		verifyActor().then(() => { if (isCurrent()) { setReady(true); loadCases(); } })
			.catch(err => { if (isCurrent()) { setError(err.message); setLoading(false); } });
		return () => { alive.current = false; loadSequence.current += 1; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!ready || !isCurrent() || incident === undefined || incident === lastIncident.current) return;
		lastIncident.current = incident;
		if (pending || storageError || busy.current || editingCaseId || form !== emptyCase) return;
		try { if (store().read()) return; }
		catch (err) { setStorageError(true); setError(err.message); return; }
		try { setForm(supportIncidentDraft(incident, scope.projectId)); }
		catch (err) { setError(err.message); }
		// Incident navigation must never replace an existing draft or replay a save.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incident, ready]);

	const updateForm = (field, value) => {
		if (!isCurrent() || pending || busy.current) return;
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const saveCase = async (event) => {
		event.preventDefault();
		if (busy.current || !ready || storageError || (editingCaseId && !pending && !pageRef.current)) return;
		busy.current = true;
		setSaving(true);
		setError("");
		setMessage("");
		let submitted;
		try {
			await verifyActor();
			submitted = pending ? store().ensure(pending) : store().read();
			if (!submitted) {
				validateSupportCaseForm(form);
				submitted = store().persist({ projectId: scope.projectId, requestId: newSupportRequestId(),
					expectedRevision, caseId: editingCaseId || null, supportCase: { ...form } });
			}
			setPending(submitted);
			setForm(submitted.command.supportCase);
			setEditingCaseId(submitted.command.caseId || "");
			setExpectedRevision(submitted.command.expectedRevision);
			guard();
			const saveSupportCase = httpsCallable(functions, "saveScervSupportCase");
			const response = await saveSupportCase(submitted.command);
			guard();
			store().confirm(submitted, response.data);
			setPending(null);
			setForm(emptyCase);
			setEditingCaseId("");
			setExpectedRevision(0);
			setMessage("Support case saved.");
			await resetHistory();
		} catch (err) {
			if (!isCurrent()) return;
			let rejected = false;
			try { rejected = submitted && store().rejectConflict(submitted, err); } catch (_) { /* Retain the original command for recovery. */ }
			if (rejected) {
				setPending(null); setForm(emptyCase); setEditingCaseId(""); setExpectedRevision(0);
				await resetHistory();
				if (isCurrent()) setError("Support case changed. No changes were saved. Select Edit again to use the current revision.");
			} else {
				setError(submitted ? "Support save is unconfirmed. Retry Save will use the original request. " + (err.message || "") : err.message || "Failed to save support case.");
			}
		} finally {
			busy.current = false;
			if (isCurrent()) setSaving(false);
		}
	};

	const editCase = (item) => {
		if (!isCurrent() || !ready || pending || busy.current || storageError || !pageRef.current?.cases.includes(item)) return;
		const revision = item.revision === undefined ? 0 : item.revision;
		if (!Number.isSafeInteger(revision) || revision < 0 || (item.projectId && item.projectId !== scope.projectId)) {
			setError("Support case scope or revision is invalid."); return;
		}
		setEditingCaseId(item.id);
		setExpectedRevision(revision);
		setForm(supportCaseForm(item));
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div className="support-cases-container">
			<div className="support-cases-header">
				<h1>Support Cases</h1>
				<p>Track customer, restaurant, order, reservation, and billing issues.</p>
			</div>

			{message && <p className="support-message">{message}</p>}
			{error && <p className="support-error" role="alert">{error}</p>}
			{loadError && <p className="support-error" role="alert">{loadError}</p>}

			<form className="support-case-form" onSubmit={saveCase}>
				<h2>{editingCaseId ? "Update Case" : "Create Case"}</h2>
				<fieldset disabled={!ready || saving || Boolean(pending) || storageError} style={{ display: "contents" }}>
				<label className="wide-field">
					Title
					<input
						required
						maxLength={180}
						value={form.title}
						onChange={(event) => updateForm("title", event.target.value)}
					/>
				</label>
				<div className="support-grid">
					<label>
						Status
						<select
							value={form.status}
							onChange={(event) => updateForm("status", event.target.value)}
						>
							<option value="open">open</option>
							<option value="waiting">waiting</option>
							<option value="investigating">investigating</option>
							<option value="resolved">resolved</option>
							<option value="closed">closed</option>
						</select>
					</label>
					<label>
						Priority
						<select
							value={form.priority}
							onChange={(event) => updateForm("priority", event.target.value)}
						>
							<option value="low">low</option>
							<option value="normal">normal</option>
							<option value="high">high</option>
							<option value="urgent">urgent</option>
						</select>
					</label>
					<label>
						Type
						<select
							value={form.type}
							onChange={(event) => updateForm("type", event.target.value)}
						>
							<option value="general">general</option>
							<option value="billing">billing</option>
							<option value="refund">refund</option>
							<option value="reservation">reservation</option>
							<option value="restaurant_ops">restaurant ops</option>
							<option value="account">account</option>
						</select>
					</label>
					<label>
						Assigned to
						<input
							value={form.assignedTo}
							onChange={(event) => updateForm("assignedTo", event.target.value)}
						/>
					</label>
				</div>
				<label className="wide-field">
					Description
					<textarea
						value={form.description}
						onChange={(event) => updateForm("description", event.target.value)}
					/>
				</label>
				<div className="support-grid">
					<label>
						Customer ID
						<input
							value={form.customerId}
							onChange={(event) => updateForm("customerId", event.target.value)}
						/>
					</label>
					<label>
						Customer email
						<input
							value={form.customerEmail}
							onChange={(event) => updateForm("customerEmail", event.target.value)}
						/>
					</label>
					<label>
						Restaurant ID
						<input
							value={form.restaurantId}
							onChange={(event) => updateForm("restaurantId", event.target.value)}
						/>
					</label>
					<label>
						Restaurant name
						<input
							value={form.restaurantName}
							onChange={(event) =>
								updateForm("restaurantName", event.target.value)
							}
						/>
					</label>
					<label>
						Order ID
						<input
							value={form.orderId}
							onChange={(event) => updateForm("orderId", event.target.value)}
						/>
					</label>
					<label>
						Reservation ID
						<input
							value={form.reservationId}
							onChange={(event) =>
								updateForm("reservationId", event.target.value)
							}
						/>
					</label>
				</div>
				</fieldset>
				<div className="support-actions">
					<button type="submit" disabled={saving || !ready || storageError}>
						{saving ? "Saving..." : pending ? "Retry Save" : "Save Case"}
					</button>
					{editingCaseId && (
						<button
							type="button"
							disabled={saving || Boolean(pending)}
							onClick={() => {
								if (!isCurrent() || busy.current || pending) return;
								setEditingCaseId("");
								setExpectedRevision(0);
								setForm(emptyCase);
							}}
						>
							Cancel Edit
						</button>
					)}
				</div>
			</form>

			<form
				className="support-filters"
				onSubmit={(event) => {
					event.preventDefault();
					if (!isCurrent() || !ready || busy.current) return;
					try { loadCases({ filters: supportHistoryFilters(draftFilters), cursors: [null] }); }
					catch (err) { setError(err.message); }
				}}
			>
				<select
					aria-label="Filter status"
					value={draftFilters.status || ""}
					onChange={(event) => setDraftFilters(prev => ({ ...prev, status: event.target.value }))}
				>
					<option value="">All statuses</option>
					<option value="open">open</option>
					<option value="waiting">waiting</option>
					<option value="investigating">investigating</option>
					<option value="resolved">resolved</option>
					<option value="closed">closed</option>
				</select>
				<input
					aria-label="Related ID"
					value={draftFilters.relatedId || ""}
					maxLength={128}
					onChange={(event) => setDraftFilters(prev => ({ ...prev, relatedId: event.target.value }))}
					placeholder="Customer, restaurant, order, reservation ID"
				/>
				<input
					aria-label="Exact case ID"
					value={draftFilters.caseId || ""}
					maxLength={128}
					onChange={(event) => setDraftFilters(prev => ({ ...prev, caseId: event.target.value }))}
					placeholder="Exact case ID"
				/>
				<button type="submit" disabled={!ready || saving}>
					Filter
				</button>
			</form>

			<div className="support-table-scroll" role="region" aria-label="Support cases" tabIndex={0}>
			<table className="support-table">
				<thead>
					<tr>
						<th>Case</th>
						<th>Status</th>
						<th>Priority</th>
						<th>Links</th>
						<th>Updated</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{cases.map((item) => (
						<tr key={item.id}>
							<td>
								<strong>{item.title}</strong>
								<span>{item.id}</span>
								<span>{item.type}</span>
							</td>
							<td>{item.status}</td>
							<td>{item.priority}</td>
							<td>
								{item.customerId && <Link to={`/customers/${item.customerId}`}>Customer</Link>}
								{item.restaurantId && <Link to={`/restaurants/${item.restaurantId}`}>Restaurant</Link>}
								{item.orderId && <Link to={`/orders/${item.orderId}`}>Order</Link>}
							</td>
							<td>{formatDate(item.updatedAt || item.createdAt)}</td>
							<td>
								<button type="button" disabled={!ready || saving || Boolean(pending) || storageError} onClick={() => editCase(item)}>
									Edit
								</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			</div>
			{loading && <p role="status">Loading support cases...</p>}
			{!loading && pageLoaded && cases.length === 0 && <p>No support cases found.</p>}
			<nav className="support-pagination" aria-label="Support history pages">
				<button type="button" disabled={!ready || loading || saving || history.cursors.length < 2}
					onClick={() => { if (isCurrent() && !busy.current && historyRef.current.cursors.length > 1) loadCases({ ...historyRef.current, cursors: historyRef.current.cursors.slice(0, -1) }); }}>
					Previous
				</button>
				<span aria-live="polite">Page {history.cursors.length}{pageLoaded ? `: ${cases.length} cases` : ""}</span>
				<button type="button" disabled={!ready || loading || saving || !nextCursor}
					onClick={() => { if (isCurrent() && !busy.current && pageRef.current?.nextCursor) loadCases({ ...historyRef.current, cursors: [...historyRef.current.cursors, pageRef.current.nextCursor] }); }}>
					Next
				</button>
			</nav>
		</div>
	);
};

const SupportCases = () => {
	const [user, loading] = useAuthState(auth);
	const location = useLocation();
	const session = useRef({ user, generation: 0 });
	if (session.current.user !== user) session.current = { user, generation: session.current.generation + 1 };
	if (loading) return <p>Loading support cases...</p>;
	let scope;
	try { scope = currentScope(user); } catch (err) { return <p role="alert" className="support-error">{err.message}</p>; }
	return <SupportCasesView key={`${JSON.stringify(scope)}:${session.current.generation}`} user={user} scope={scope} incident={location.state?.supportIncident} />;
};

export default SupportCases;
